/**
 * 점검 상태 표시 메타 — 상태별 아이콘 컴포넌트.
 *
 * 라벨·색은 `lib/inspections.ts`의 `inspectionStatusConfig`에 있다. 아이콘만 여기로
 * 분리한 이유는 `lib/`이 순수 데이터 계층(`.ts`)이라 컴포넌트를 들고 있으면
 * lib → components 역방향 의존이 생기기 때문이다(표시 계층 분리는
 * `components/reports/reportTypeDisplay.ts` 선례를 따른다).
 *
 * ⚠️ **exhaustive `Record<...>`를 유지할 것.** 인덱스 시그니처(`{ [k: string]: ... }`)로
 * 풀면 상태가 늘 때 tsc가 누락을 못 잡고 조용히 fallback으로 떨어져,
 * "전 상태가 물음표"가 된다.
 */
import { InspectionStatus } from '../../lib/inspections';
import type { IconComponent } from '../icons';
import {
  ArrowPathIcon,
  CheckIcon,
  CircleIcon,
  ClockIcon,
  HelpCircleIcon,
} from '../icons';

/**
 * 상태별 아이콘. 키는 `inspectionStatusConfig`와 같은 집합이다 —
 * enum 4종 + 서버가 실제로 내려주는 `DRAFT`(진행중과 동일 취급).
 */
export const INSPECTION_STATUS_ICON: Record<InspectionStatus | 'DRAFT', IconComponent> = {
  [InspectionStatus.SCHEDULED]: CircleIcon,
  DRAFT: ArrowPathIcon,
  [InspectionStatus.IN_PROGRESS]: ArrowPathIcon,
  [InspectionStatus.PENDING_REVIEW]: ClockIcon,
  [InspectionStatus.COMPLETED]: CheckIcon,
};

/**
 * 상태 아이콘 안전 조회 — **맵을 직접 인덱싱하지 말고 이걸 쓸 것.**
 *
 * 목록 API의 `status`는 `string`이라 매핑에 없는 값이 올 수 있다. 그대로 인덱싱해
 * `undefined`를 `<Icon />`으로 렌더하면 "Element type is invalid" 로 화면이 통째로 죽는다.
 * 표시 계층은 모르는 상태를 만나도 물음표로 떨어지는 게 맞다.
 */
export function getInspectionStatusIcon(status: string | undefined | null): IconComponent {
  if (!status) return HelpCircleIcon;
  return INSPECTION_STATUS_ICON[status as InspectionStatus | 'DRAFT'] ?? HelpCircleIcon;
}
